import React from 'react';
import { Modal, Button, Spin, Icon, Select } from 'antd';
import request from 'superagent';
import { CountryCode, GLGlobal, LanguageDropdown, LanguageLocale, MessageHelper, NotificationType, OidcProxy, PrimaryLanguageLocale } from 'gl-commonui';
import "./privacypolicy.less"
import { SchoolLocale } from '@app/locales/localeid';
import { IUserService } from '@app/service/users';
import { CountryToPrimaryLanguageLocaleMap, isRussiaPrivacyPolicyRole, lazyInject, privacyPolicyAllowedRolesKoreaExistingUsers, TranslationText, TYPES, UserType, scrollMou, scrollMouseAnimation } from '@app/util';
import Lottie from 'lottie-react';

type LocaleMessages = {
    [key: string]: string;
};

const privacyPolicyLocaleMessages: { [key: string]: LocaleMessages } = {
    en: require('@app/locales/en.json'),
    ko: require('@app/locales/ko.json'),
    ru: require('@app/locales/ru.json'),
};

const formatMessageByLanguage = (language: string, localeId: string): string => {
    const normalized = (language || '').toLowerCase();
    const locale = normalized.indexOf('ko') === 0 ? 'ko' : normalized.indexOf('ru') === 0 ? 'ru' : 'en';
    const messages = privacyPolicyLocaleMessages[locale] || privacyPolicyLocaleMessages.en;
    return messages[localeId] || localeId;
};

export const PP_FAILED_LOAD = {
    en: {
        message: 'Unable to load the Privacy Policy at this time.',
        reload: 'Try again'
    },
    ko: {
        message: '현재 개인정보 처리방침을 불러올 수 없습니다.',
        reload: '다시 시도'
    },
    ru: {
        message: 'Не удалось загрузить Политику конфиденциальности в данный момент.',
        reload: 'Попробовать снова'
    }
};


interface PrivacyPolicyState {
    isModalVisible: boolean;
    loading: boolean;
    htmlContent: any;
    isLanguageEnglish: boolean;
    isScrolledToBottom: boolean;
    shouldRender: boolean;
    userAcceptedPrivacyPolicy: any;
    isNotFoundPage: boolean;
    primaryLanguage: string;
    currentLanguage: string;
    showDeclineModal: boolean;
    loadError: boolean;
}

interface PrivacyPolicyProps {
    isPrivacyPolicyAccepted?: boolean;
    countryCode?: string;
    userType: string;
    delayMilliSeconds: number;
    invitationType?: string;
    handleIsPrivacyPolicyAccepted?: (params) => void;
    handleUserIpAddress?: (params) => void;
}

class PrivacyPolicy extends React.Component<PrivacyPolicyProps, PrivacyPolicyState> {
    pathName
    contentRef: React.RefObject<HTMLDivElement>;
    @lazyInject(TYPES.IUserService)
    userService: IUserService;
    static defaultProps: Partial<PrivacyPolicyProps> = {
        delayMilliSeconds: 0,
    };
    constructor(props) {
        super(props);
        this.state = {
            isModalVisible: false,
            loading: false,
            htmlContent: '',
            isLanguageEnglish: false,
            isScrolledToBottom: false,
            shouldRender: false,
            userAcceptedPrivacyPolicy: false,
            isNotFoundPage: false,
            primaryLanguage: '',
            currentLanguage: '',
            showDeclineModal: false,
            loadError: false,
        };

        this.contentRef = React.createRef<HTMLDivElement>();
    }

    componentDidMount() {
        const { userType, delayMilliSeconds, countryCode, invitationType } = this.props;
        let userCountryCode = localStorage.getItem("gl-common.countrycode");

        setTimeout(() => {
            this.setState({ shouldRender: true }, () => {
                const { loggedin } = GLGlobal.loginInfo();

                const NotFoundPageElementExists = document.querySelector(".gl-not-found");
                if (NotFoundPageElementExists) {
                    this.setState({ isNotFoundPage: true });
                    return;
                }

                if (!userCountryCode && countryCode) {
                    userCountryCode = countryCode;
                }

                if (userType === UserType.Existing && loggedin && userCountryCode === CountryCode.Korea) {
                    this.setState({ primaryLanguage: userCountryCode })
                    this.checkUserEligibilty(userCountryCode);
                }

                this.setState({ loading: true });
                if (userType === UserType.New && countryCode === CountryCode.Korea) {
                    this.setState({ isModalVisible: true, primaryLanguage: CountryCode.Korea, currentLanguage: PrimaryLanguageLocale.Korean })
                    this.fetchHtmlContent(countryCode, PrimaryLanguageLocale.Korean);
                }
                const isRussiaNewUser = userType === UserType.New
                    && countryCode === CountryCode.Russsia
                    && isRussiaPrivacyPolicyRole(invitationType);
                if (isRussiaNewUser) {
                    this.setState({ isModalVisible: true, primaryLanguage: CountryCode.Russsia, currentLanguage: PrimaryLanguageLocale.Russian })
                    this.fetchHtmlContent(countryCode, PrimaryLanguageLocale.Russian);
                }
            });
        }, delayMilliSeconds);
    }

    componentDidUpdate(prevProps: Readonly<{}>, prevState: Readonly<PrivacyPolicyState>): void {
        if (prevState.htmlContent !== this.state.htmlContent) {
            setTimeout(() => {
                this.attachScrollListener();
            }, 0);
        }
    }

    getModalText = (localeId: string): string => {
        return formatMessageByLanguage(this.state.currentLanguage, localeId);
    }

    attachScrollListener = () => {
        if (this.contentRef.current) {
            this.contentRef.current.getElementsByClassName('site-info')[0].addEventListener('scroll', this.handleScroll);
        }
    };

    handleScroll = () => {
        const contentElement = this.contentRef.current.getElementsByClassName('site-info')[0];
        if (contentElement) {
            const isAtBottom = contentElement.scrollTop + contentElement.clientHeight >= contentElement.scrollHeight - 2;
            if (isAtBottom) {
                this.setState({ isScrolledToBottom: true });
            }
        }
    };

    async checkUserEligibilty(userRegion) {
        let hasAccepted = sessionStorage.getItem('acceptedPrivacyPolicy');
        let storedUserId = sessionStorage.getItem('acceptedPrivacyPolicyUserId');
        let currentUserId = GLGlobal.loginInfo().profile.sub;
        let userRoles = GLGlobal.loginInfo().profile.roles || [];

        const allowedRoles: String[] = Object.values(privacyPolicyAllowedRolesKoreaExistingUsers);
        const hasAllowedRole = userRoles.some(role => allowedRoles.includes(role));
        
        if (!hasAllowedRole) {
            this.setState({ userAcceptedPrivacyPolicy: true, isModalVisible: false });
            return;
        }

        if (hasAccepted === "true" && storedUserId === currentUserId) {
            this.setState({ userAcceptedPrivacyPolicy: true, isModalVisible: false });
            return;
        }

        if (userRegion == CountryCode.Korea) {
            try {
                let hasUserAcceptedPrivacyPolicy = await this.userService.hasUserAcceptedPrivacyPolicy({ userId: currentUserId })

                this.setState({ userAcceptedPrivacyPolicy: hasUserAcceptedPrivacyPolicy });

                if (!hasUserAcceptedPrivacyPolicy) {
                    this.setState({ isModalVisible: true, currentLanguage: PrimaryLanguageLocale.Korean });
                    this.fetchHtmlContent(userRegion, PrimaryLanguageLocale.Korean);
                }
            } catch (error) {
                console.error("Error checking privacy policy:", error);
            }
        }
    }

    handleOk = async () => {
        let userIPAddress = await request.get(`https://ipapi.co/json?key=${process.env.ipApiKey}`).then((result) => { return result; });

        let currentUserId = GLGlobal.loginInfo().profile.sub;

        if (this.props.userType === UserType.Existing) {
            const params = { userId: currentUserId, ipAddress: userIPAddress.body.ip };

            this.userService.savePrivacyPolicyAcceptanceInfo(params);

            sessionStorage.setItem('acceptedPrivacyPolicy', 'true');
            sessionStorage.setItem('acceptedPrivacyPolicyUserId', currentUserId);
        } else {
            this.props.handleUserIpAddress(userIPAddress.body.ip)
            this.props.handleIsPrivacyPolicyAccepted(true);
        }

        this.setState({ isModalVisible: false });
    };

    handleCancel = () => {
        if (this.props.userType === UserType.New) {
            MessageHelper.Message(NotificationType.Failed, GLGlobal.intl.formatMessage({ id: SchoolLocale.PrivacyPolicyDeclineMessageForNewUser }));
            this.props.handleIsPrivacyPolicyAccepted(false);
        } else {
            this.setState({ showDeclineModal: true })
        }

        this.setState({ isModalVisible: false });
    };

    handleCloseDeclineModal = () => {
        OidcProxy.signoutRedirect();
        sessionStorage.removeItem("acceptedPrivacyPolicy");
    }

    changeLanguage = async (language: string) => {
        const { primaryLanguage, currentLanguage } = this.state;
        if (language === currentLanguage) return;
        this.setState({ currentLanguage: language });
        await this.fetchHtmlContent(primaryLanguage, language);
    }

    getFailedLoadMsg = () => {
        const normalized = (this.state.currentLanguage || '').toLowerCase();
        const locale = normalized.indexOf('ko') === 0 ? 'ko' : normalized.indexOf('ru') === 0 ? 'ru' : 'en';
        return PP_FAILED_LOAD[locale];
    }

    handleRetryLoad = () => {
        const { primaryLanguage, currentLanguage } = this.state;
        this.fetchHtmlContent(primaryLanguage, currentLanguage);
    }

    async fetchHtmlContent(region: string, language: string) {
        this.setState({ loading: true, loadError: false });
        await request.get(`${GLGlobal.authorityUrl()}/Copyright/privacy-policy?region=${region}&language=${language}`).then(res => (res.text))
            .then(data => {
                const parser = new DOMParser();
                const doc = parser.parseFromString(data, 'text/html');
                const siteBody = doc.querySelector('.site-body');
                const bodyContent = siteBody ? siteBody.innerHTML : data;
                this.setState({ loading: false, htmlContent: bodyContent });
            })
            .catch(error => {
                console.error('There has been a problem with your fetch operation:', error);
                this.setState({ loading: false, loadError: true });
            });
    }

    renderActionButtons = () => {
        const { isScrolledToBottom } = this.state;
        return (
            <div className='action-btns'>
                <Button className='decline-btn' key="reject" type="danger" onClick={this.handleCancel}>
                    {this.getModalText(SchoolLocale.PrivacyPolicyDecline)}
                </Button>
                <Button key="accept" type="primary" onClick={this.handleOk} disabled={!isScrolledToBottom} className={!this.state.isScrolledToBottom ? "accept-btn-disable " : "accept-btn"}>
                    {this.getModalText(SchoolLocale.PrivacyPolicyAcceptBtn)}
                </Button>
            </div>
        );
    };

    renderTranslationButton = () => {
        const { primaryLanguage } = this.state;
        const enSvg = () => (<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" x="0" y="0" viewBox="0 0 512 512" xmlSpace="preserve"><circle cx="256" cy="256" r="256" fill="#F0F0F0"/><g fill="#D80027"><path d="M244.87 256H512c0-23.106-3.08-45.49-8.819-66.783H244.87V256zM244.87 122.435h229.556a257.35 257.35 0 00-59.07-66.783H244.87v66.783zM256 512c60.249 0 115.626-20.824 159.356-55.652H96.644C140.374 491.176 195.751 512 256 512zM37.574 389.565h436.852a254.474 254.474 0 0028.755-66.783H8.819a254.474 254.474 0 0028.755 66.783z"/></g><path fill="#0052B4" d="M118.584 39.978h23.329l-21.7 15.765 8.289 25.509-21.699-15.765-21.699 15.765 7.16-22.037a257.407 257.407 0 00-49.652 55.337h7.475l-13.813 10.035a255.58 255.58 0 00-6.194 10.938l6.596 20.301-12.306-8.941a253.567 253.567 0 00-8.372 19.873l7.267 22.368h26.822l-21.7 15.765 8.289 25.509-21.699-15.765-12.998 9.444A258.468 258.468 0 000 256h256V0c-50.572 0-97.715 14.67-137.416 39.978zm9.918 190.422l-21.699-15.765L85.104 230.4l8.289-25.509-21.7-15.765h26.822l8.288-25.509 8.288 25.509h26.822l-21.7 15.765 8.289 25.509zm-8.289-100.083l8.289 25.509-21.699-15.765-21.699 15.765 8.289-25.509-21.7-15.765h26.822l8.288-25.509 8.288 25.509h26.822l-21.7 15.765zM220.328 230.4l-21.699-15.765L176.93 230.4l8.289-25.509-21.7-15.765h26.822l8.288-25.509 8.288 25.509h26.822l-21.7 15.765 8.289 25.509zm-8.289-100.083l8.289 25.509-21.699-15.765-21.699 15.765 8.289-25.509-21.7-15.765h26.822l8.288-25.509 8.288 25.509h26.822l-21.7 15.765zm0-74.574l8.289 25.509-21.699-15.765-21.699 15.765 8.289-25.509-21.7-15.765h26.822l8.288-25.509 8.288 25.509h26.822l-21.7 15.765z"/></svg>);
        const ruSvg = () => (<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" x="0" y="0" viewBox="0 0 512 512" xmlSpace="preserve"><circle cx="256" cy="256" r="256" fill="#F0F0F0"/><path fill="#0052B4" d="M496.077 345.043C506.368 317.31 512 287.314 512 256s-5.632-61.31-15.923-89.043H15.923C5.633 194.69 0 224.686 0 256s5.633 61.31 15.923 89.043L256 367.304l240.077-22.261z"/><path fill="#D80027" d="M256 512c110.071 0 203.906-69.472 240.077-166.957H15.923C52.094 442.528 145.929 512 256 512z"/></svg>);
        const koSvg = () => (<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 512 512"><circle cx="256" cy="256" r="256" fill="#F0F0F0"/><path fill="#D80027" d="M345.043 256c0 22.261-39.866 77.913-89.043 77.913S166.957 278.261 166.957 256c0-49.178 39.866-89.043 89.043-89.043s89.043 39.865 89.043 89.043z"/><path fill="#0052B4" d="M345.043 256c0 49.178-39.866 89.043-89.043 89.043S166.957 305.178 166.957 256"/><path d="M353.427 319.639H386.818V341.9H353.427z" transform="rotate(-45.001 370.12 330.776)"/><path d="M314.07 358.994H347.461V381.255H314.07z" transform="rotate(-45.001 330.763 370.13)"/><path d="M400.656 366.851H434.047V389.112H400.656z" transform="rotate(-45.001 417.35 377.989)"/><path d="M361.299 406.213H394.69V428.474H361.299z" transform="rotate(-45.001 377.992 417.35)"/><path d="M377.04 343.247H410.431V365.508H377.04z" transform="rotate(-45.001 393.733 354.384)"/><path d="M337.694 382.602H371.085V404.863H337.694z" transform="rotate(-45.001 354.387 393.738)"/><path d="M329.544 126.805H418.587V149.066H329.544z" transform="rotate(-134.999 374.067 137.936)"/><path d="M314.081 130.749H347.472V153.009H314.081z" transform="rotate(-134.999 330.778 141.88)"/><path d="M353.427 170.098H386.818V192.358H353.427z" transform="rotate(-134.999 370.124 181.23)"/><path d="M361.282 83.526H394.673V105.787H361.282z" transform="rotate(-134.999 377.98 94.658)"/><path d="M400.651 122.879H434.042V145.14H400.651z" transform="rotate(-134.999 417.348 134.01)"/><path d="M69.81 386.542H158.853V408.803H69.81z" transform="rotate(45.001 114.336 397.673)"/><path d="M140.919 382.6H174.31V404.86H140.919z" transform="rotate(45.001 157.62 393.73)"/><path d="M101.569 343.252H134.96V365.513H101.569z" transform="rotate(45.001 118.269 354.382)"/><path d="M117.031 339.316H206.074V361.577H117.031z" transform="rotate(45.001 161.557 350.446)"/><path d="M69.81 103.201H158.853V125.462H69.81z" transform="rotate(134.999 114.332 114.331)"/><path d="M93.412 126.806H182.455V149.067H93.412z" transform="rotate(134.999 137.934 137.937)"/><path d="M117.038 150.428H206.081V172.688H117.038z" transform="rotate(134.999 161.56 161.558)"/></svg>);

        const languageMap = {
            EN: { code: PrimaryLanguageLocale.English, flag: enSvg, label: LanguageLocale.en },
            KR: { code: PrimaryLanguageLocale.Korean, flag: koSvg, label: LanguageLocale.ko },
            RU: { code: PrimaryLanguageLocale.Russian, flag: ruSvg, label: LanguageLocale.ru }
        }

        const langOptions = [languageMap[primaryLanguage], languageMap["EN"]].filter(Boolean);
        return (
            <LanguageDropdown
                options={langOptions}
                current={this.state.currentLanguage as PrimaryLanguageLocale}
                onChange={this.changeLanguage}
            />
        );
    };

    render() {
        const { loading, htmlContent, userAcceptedPrivacyPolicy, isNotFoundPage, isScrolledToBottom, showDeclineModal, loadError } = this.state;

        if (isNotFoundPage || !this.state.shouldRender || userAcceptedPrivacyPolicy) {
            return null;
        }

        return (
            <div>
                {<Modal
                    visible={this.state.isModalVisible}
                    onOk={this.handleOk}
                    onCancel={this.handleCancel}
                    width={860}
                    maskClosable={false}
                    closable={false}
                    destroyOnClose
                    centered
                    footer={!loading && !loadError ? [
                        <p className='policy-agreement'> {this.getModalText(SchoolLocale.PrivacyPolicyAcceptance)} </p>,
                        this.renderActionButtons()
                    ] : null}
                >
                    {!loading && this.renderTranslationButton()}
                    {loading ? (
                        <div className='spinner-container'><Spin /></div>
                    ) : loadError ? (
                        <div className='pp-load-error'>
                            <p className='pp-load-error__message'>{this.getFailedLoadMsg().message}</p>
                            <Button type='primary' className='pp-load-error__retry' onClick={this.handleRetryLoad}>
                                {this.getFailedLoadMsg().reload}
                            </Button>
                        </div>
                    ) : (
                        <div className='scroll-container'>
                            <div ref={this.contentRef} dangerouslySetInnerHTML={{ __html: htmlContent }} />
                            {!isScrolledToBottom &&
                                <div>
                                    <div className="bounce-arrow">
                                        <Lottie className='scroll-icon' animationData={scrollMouseAnimation} loop={true} />
                                    </div>
                                    <div className={`scroll-indicator`}></div>
                                </div>}
                        </div>
                    )}
                </Modal>}
                <Modal
                    visible={showDeclineModal}
                    maskClosable={false}
                    closable={false}
                    centered
                    width={400}
                    footer={[
                        <Button key="cancel" onClick={this.handleCloseDeclineModal}>{this.getModalText(SchoolLocale.PrivacyPolicyDeclineModalOkBtn)}</Button>,
                    ]}
                >
                    <p>{this.getModalText(SchoolLocale.PrivacyPolicyDeclineMessageForExistingUser)}</p>
                </Modal>
            </div>
        );
    }
}

export default PrivacyPolicy;


